import React, { useState } from "react";
import { Button, Card, Col, Container, Row, Spinner, Stack } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { fetchUsers, fetchCMUsers, deleteUser, fetchUserCases } from "../../services/httpClient";
import { User } from "../../types/user";
import useApp from "../../utils/useApp";
import PageTitle from "../ui/PageTitle";
import CaseReassignmentModal from "./CaseReassignmentModal";
import IconButton from "../ui/IconButton";
import { faInfoCircle } from "@fortawesome/free-solid-svg-icons";

interface UserTabState {
  users: User[];
  setUsers: any;
  setCases: any;
  type: string;
  displayType: string;
  setShowToast: any;
  setToastText: any;
  setToastBody: any;
}

const UserTab = ({ users, setUsers, setCases, type, displayType, setShowToast, setToastText, setToastBody }: UserTabState) => {
  const { auth }: any = useApp();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isDeleting, setIsDeleting] = useState<boolean>(false);
  const [userToDelete, setUserToDelete] = useState<User>();
  const [showCaseReassignmentModal, setShowCaseReassignmentModal] = useState<boolean>(false);
  const [casesToReassign, setCasesToReassign] = useState<any[]>([]);
  const [cmUsers, setCmUsers] = useState<any[]>([]);

  let navigate = useNavigate();

  const editUser = (e: any) => {
    navigate(`/users/user/${e}`);
  };

  const CreateNewUser = (e: any) => {
    navigate(`/users/user/create/${type}`);
  };

  const onClickDelete = (e: any) => {
    setIsDeleting(true);
    setUserToDelete(e);
  };

  const onClickDeleteFinal = () => {
    deleteUser(userToDelete?.id)
      .then(() => {
        if (userToDelete?.cases && userToDelete.cases.length > 0) {
          // show reassignment modal
          setIsDeleting(false);
          setCasesToReassign(userToDelete.cases);
          setShowCaseReassignmentModal(true);

          fetchCMUsers()
            .then((res: any) => {
              const users = res[0].data
                .filter((u: any) => u.id !== userToDelete.id) // Exclude the deleted user
                .map((i: any) => {
                  return {
                    id: i.id,
                    key: i.id,
                    name: i.firstname.length === 0 ? i.email : `${i.firstname} ${i.lastname}`,
                  };
                });
              setCmUsers(users);
            })
            .catch((err: any) => {
              console.log(err);
              setIsLoading(false);
            });
        } else {
          // No cases to reassign, complete deletion immediately
          setIsLoading(true);
          completeUserDeletion();
        }
      })
      .catch((err: any) => {
        console.log(err);
        setIsLoading(false);
      });
  };

  const completeUserDeletion = async () => {
    setShowToast(true);
    setToastText("User Deleted");
    setToastBody(userToDelete?.firstname + " " + userToDelete?.lastname);
    setUserToDelete(undefined);
    setIsDeleting(false);

    await getCases();

    fetchUsers()
      .then((res: any) => {
        const userList = res[0].data;
        setUsers(userList);
        setIsLoading(false);
      })
      .catch((err: any) => {
        console.log(err);
        setIsLoading(false);
      });
  };

  const getCases = async () => {
    await fetchUserCases()
      .then((res: any) => {
        var c = res[0].data;
        setCases(c);
      })
      .catch((err: any) => {
        console.log(err);
      });
  };

  const handleCaseReassignmentComplete = () => {
    setShowCaseReassignmentModal(false);
    setIsLoading(true);
    completeUserDeletion();
  };

  const onClickCancelDelete = () => {
    setIsDeleting(false);
    setUserToDelete(undefined);
  };

  const getCaseTooltip = (user: any) => {
    const tooltipParts = [];

    if (user.waitlistCases > 0) {
      tooltipParts.push(`${user.waitlistCases} Waitlist Case${user.waitlistCases > 1 ? "s" : ""}`);
    }

    if (user.inactiveCases > 0) {
      tooltipParts.push(`${user.inactiveCases} Inactive Case${user.inactiveCases > 1 ? "s" : ""}`);
    }

    return tooltipParts.join(", ");
  };

  const renderDelete = () => {
    return (
      <Card className="bg-light">
        <Card.Body>
          <Stack direction="horizontal" gap={3}>
            <Stack style={{ justifyContent: "center" }}>
              <Card.Subtitle>Are you sure you want to delete {userToDelete?.firstname + " " + userToDelete?.lastname}?</Card.Subtitle>
            </Stack>
            <Button className="app-danger-color button" onClick={() => onClickDeleteFinal()}>
              Confirm Delete
            </Button>
            <div className="vr" />
            <Button className="app-primary-color button" onClick={() => onClickCancelDelete()}>
              Cancel
            </Button>
          </Stack>
        </Card.Body>
      </Card>
    );
  };

  const renderCannotDeleteThemself = () => {
    return (
      <Card className="bg-light">
        <Card.Body>
          <Stack direction="horizontal" gap={3}>
            <Stack style={{ justifyContent: "center" }}>
              <Card.Subtitle>You cannot delete your own {displayType} account.</Card.Subtitle>
            </Stack>
            <Button className="app-primary-color button" onClick={() => onClickCancelDelete()}>
              Cancel
            </Button>
          </Stack>
        </Card.Body>
      </Card>
    );
  };

  const renderCannotDeleteAll = () => {
    return (
      <Card className="bg-light">
        <Card.Body>
          <Stack direction="horizontal" gap={3}>
            <Stack style={{ justifyContent: "center" }}>
              <Card.Subtitle>You cannot delete the only {displayType} account.</Card.Subtitle>
            </Stack>
            <Button className="app-primary-color button" onClick={() => onClickCancelDelete()}>
              Cancel
            </Button>
          </Stack>
        </Card.Body>
      </Card>
    );
  };

  return (
    <Row>
      <Col xs={12}>
        <CaseReassignmentModal
          show={showCaseReassignmentModal}
          cases={casesToReassign}
          cmUsers={cmUsers}
          deletedUserId={userToDelete?.id}
          onComplete={handleCaseReassignmentComplete}
          setShowToast={setShowToast}
          setToastText={setToastText}
          setToastBody={setToastBody}
        />
        <Stack gap={3}>
          <PageTitle
            title={
              type === "org_admin"
                ? "Org Admin Users"
                : type === "admin"
                  ? "Admin Users"
                  : type === "case_manager"
                    ? "Case Manager Users"
                    : type === "res_support"
                      ? "Residential Support Users"
                      : "Support Users"
            }
            type="section"
            buttonTitle="Create User"
            onButtonClick={CreateNewUser}
          />
          {isDeleting ? (
            userToDelete?.id === auth?.profile?.id ? (
              <>{renderCannotDeleteThemself()}</>
            ) : users?.length === 1 && type === "org_admin" ? (
              <>{renderCannotDeleteAll()}</>
            ) : (
              <>{renderDelete()}</>
            )
          ) : isLoading ? (
            <Container className="div-main">
              <Button size="sm" className="spinner-button spinner-button-main">
                <Spinner animation="border" style={{ color: "#F6893D" }} />
              </Button>
            </Container>
          ) : (
            <>
              {users.length === 0 && <p className="mt-2">No {displayType} User Records</p>}
              {users.map((user: any) => (
                <Card key={user.id} className="bg-light">
                  <Card.Body>
                    <Stack direction="horizontal" gap={3}>
                      <Stack className="cursor-pointer" onClick={(e) => editUser(user.id)}>
                        <Card.Subtitle>
                          {user.firstname} {user.preferredname ? "'" + user.preferredname + "'" : ""} {user.lastname}
                        </Card.Subtitle>
                        <Card.Subtitle className="text-muted fw-normal small mt-1">{user.email} </Card.Subtitle>
                      </Stack>
                      {user.activeCases > 0 && (
                        <Card.Subtitle className="text-muted fw-normal small mt-1">
                          {`${user.activeCases} Active Case${user.activeCases > 1 ? "s" : ""}`}{" "}
                        </Card.Subtitle>
                      )}
                      {(user.waitlistCases > 0 || user.inactiveCases > 0) && (
                        <IconButton
                          icon={faInfoCircle}
                          tooltipText={getCaseTooltip(user)}
                          className="opacity-75 app-primary-color-inverse mt-1"
                        />
                      )}
                      <Button className="app-secondary-color button" onClick={(e) => editUser(user.id)}>
                        Edits
                      </Button>
                      <div className="vr" />
                      <Button className="app-danger-color button" onClick={(e) => onClickDelete(user)}>
                        Delete
                      </Button>
                    </Stack>
                  </Card.Body>
                </Card>
              ))}
            </>
          )}
        </Stack>
      </Col>
    </Row>
  );
};

export default UserTab;
